// src/components/widgets/avatar-textfield.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack, Avatar, Typography, Link } from '@mui/material';
import { getInitials } from 'src/utils/get-initials';
import { getAvatar } from 'src/services/AssetApi';

const AvatarTextField = ({
  avatarSrc,
  avatarName,
  value,
  subValue,
  avatarSx = {},
  sx = {},
  onClick,
}) => {
  const isClickable = typeof onClick === 'function';
  const {
    size = { sm: 40, md: 44 },
    color = 'inherit',
    fontSize: customFontSize,
  } = avatarSx;

  const [mFontWeight, setFontWeight] = useState(400);
  const [mFontSize, setFontSize] = useState('0.75rem');
  const [name, setName] = useState('');
  const [src, setSrc] = useState('');
  const [isImageLoaded, setImageLoaded] = useState(false);
  const [shouldRenderAvatar, setShouldRenderAvatar] = useState(false);

  useEffect(() => {
      // Reset everything immediately to avoid showing stale data
    setSrc('');
    setName('');
    setImageLoaded(false);
    setShouldRenderAvatar(false);

    // Load image
    if (avatarSrc) {
      const img = new Image();
      const finalSrc = getAvatar(avatarSrc);
      img.src = finalSrc;

      img.onload = () => {
        setSrc(finalSrc);
        setImageLoaded(true);
        setShouldRenderAvatar(true);
      };
      img.onerror = () => {
        setImageLoaded(false);
        setShouldRenderAvatar(true); // fallback to initials
      };
    } else {
      setShouldRenderAvatar(true);
    }

    if (typeof avatarName === 'string' && avatarName.trim()) {
      const trimmedName = avatarName.trim();
      setName(trimmedName);
      if(customFontSize){
        setFontWeight(500);
        setFontSize(customFontSize);
      }else{
        const length = trimmedName.length;
        if (length > 3) {
          setName(getInitials(trimmedName));
          setFontWeight(500);
          setFontSize('0.875rem');
        } else {
          if (length === 2) {
            setFontWeight(500);
            setFontSize('0.8rem');
          } 
        }
      }
    }
  }, [avatarSrc, avatarName, customFontSize]);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {shouldRenderAvatar && (
        <Avatar
          src={isImageLoaded ? src : undefined}
          sx={{
            width: size,
            height: size,
            fontWeight: mFontWeight,
            fontSize: mFontSize,
            color: isClickable ? color : 'inherit',
            cursor: isClickable ? 'pointer' : 'default',
          }}
        >
          {name}
        </Avatar>
      )}

      <Stack spacing={0.25} sx={{ ...sx }}>
        {isClickable ? (
          <Link
            component="button"
            variant="subtitle2"
            onClick={onClick}
            sx={{ color: 'inherit' }}
            underline="hover"
          >
            {value}
          </Link>
        ) : (
          <Typography
            variant="subtitle2"
            sx={{ color: 'inherit' }}
          >
            {value}
          </Typography>
        )}

        {subValue && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
          >
            {subValue}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

AvatarTextField.propTypes = {
  avatarSrc: PropTypes.string,
  avatarName: PropTypes.string,
  value: PropTypes.string,
  avatarSx: PropTypes.shape({
    size: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.object, // responsive size: { sm, md }
    ]),
    color: PropTypes.string,
    fontSize: PropTypes.string,
  }),
  sx: PropTypes.object,
  onClick: PropTypes.func,
};

export default AvatarTextField;
